import React, { useState, useMemo, useEffect } from "react";
import { Link, Navigate, useLocation } from "react-router-dom";
import axios from "axios";
import { jsPDF } from "jspdf";
import { useContent, useLang } from "../i18n/LanguageContext";
import { useAuth } from "../i18n/AuthContext";
import { ACCOUNT } from "../i18n/shared";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import {
  ArrowLeft, LogOut, Lock, Mail, User as UserIcon, Wallet, TrendingUp, Percent,
  PiggyBank, ChevronRight, ShieldCheck, Inbox, LayoutGrid, Landmark, ArrowLeftRight,
  FileText, LineChart, Target, Download, Plus, ArrowUpFromLine, ArrowDownToLine,
  Phone, Globe, Building2, User2,
} from "lucide-react";
import LanguageSwitcher from "../components/LanguageSwitcher";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COUNTRIES = [
  "Deutschland", "Österreich", "Schweiz", "Luxemburg", "Frankreich", "Belgien",
  "Niederlande", "Italien", "Spanien", "Portugal", "Irland", "Dänemark",
  "Schweden", "Finnland", "Polen", "Tschechien", "Liechtenstein", "Andere",
];

function TopBar() {
  const c = useContent();
  const { user, logout } = useAuth();
  return (
    <header className="sticky top-0 z-40 bg-white/95 backdrop-blur-md border-b border-[#eee]">
      <div className="max-w-6xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-md bg-[#16233d] flex items-center justify-center">
            <span className="font-serif-lux text-2xl text-[#b08d57] leading-none">B</span>
          </div>
          <div className="leading-tight">
            <div className="font-serif-lux text-xl text-[#16233d] font-semibold">{c.nav.brand}</div>
            <div className="text-[10px] tracking-[0.2em] text-[#8a8f9c]">{c.nav.brandSub}</div>
          </div>
        </Link>
        <div className="flex items-center gap-4">
          <LanguageSwitcher />
          <Link to="/" className="hidden sm:flex items-center gap-1.5 text-sm text-[#3a4258] hover:text-[#b08d57] transition-colors">
            <ArrowLeft size={16} /> {c.portal.backHome}
          </Link>
          {user && (
            <button onClick={logout} className="btn-outline-navy rounded-md px-4 py-2 text-sm font-medium flex items-center gap-2">
              <LogOut size={15} /> {c.portal.logout}
            </button>
          )}
        </div>
      </div>
    </header>
  );
}

function Login({ initialMode = "login" }) {
  const c = useContent();
  const p = c.portal;
  const { login, register } = useAuth();
  const { lang } = useLang();
  const [mode, setMode] = useState(initialMode);
  const [accountType, setAccountType] = useState("private");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [country, setCountry] = useState("");
  const [phone, setPhone] = useState("");
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [forgot, setForgot] = useState(false);
  const [forgotBusy, setForgotBusy] = useState(false);

  const handleForgot = async (e) => {
    e.preventDefault();
    if (!emailValid(email)) { toast.error(p.errEmailInvalid); return; }
    setForgotBusy(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email, lang });
      toast.success(p.forgotOk);
      setForgot(false);
    } catch {
      toast.error(p.errGeneric);
    } finally {
      setForgotBusy(false);
    }
  };

  const isRegister = mode === "register";
  const emailValid = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

  const submit = async (e) => {
    e.preventDefault();
    if (isRegister && !name.trim()) {
      toast.error(p.errGeneric);
      return;
    }
    if (!emailValid(email)) {
      toast.error(p.errEmailInvalid);
      return;
    }
    if (password.length < 6) {
      toast.error(p.errPassword);
      return;
    }
    if (isRegister && !consent) {
      toast.error(p.errConsent);
      return;
    }
    setBusy(true);
    const res = isRegister
      ? await register({ name, email, password, accountType, country, phone, consent, lang })
      : await login(email, password);
    setBusy(false);
    if (res.ok) {
      toast.success(isRegister ? p.registerOk : p.loginOk);
    } else if (res.error === "email_exists") {
      toast.error(p.errEmailExists);
    } else if (res.error === "invalid_credentials") {
      toast.error(p.loginErr);
    } else {
      toast.error(p.errGeneric);
    }
  };

  const fillDemo = () => {
    setMode("login");
    setEmail(ACCOUNT.email);
    setPassword(ACCOUNT.password);
  };

  const inputCls =
    "w-full rounded-md border border-[#e7e2d8] bg-white pl-11 pr-4 py-3 text-sm text-[#16233d] placeholder-[#9aa0ad] focus:outline-none focus:border-[#b08d57] focus:ring-2 focus:ring-[#b08d57]/20 transition";

  return (
    <div className="min-h-[calc(100vh-5rem)] flex items-center justify-center bg-[#faf8f4] px-6 py-16">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl border border-[#efe9dd] p-8 shadow-[0_30px_60px_-40px_rgba(22,35,61,0.3)]">
          <div className="w-12 h-12 rounded-lg bg-[#faf3e8] flex items-center justify-center mb-5">
            <ShieldCheck size={24} className="text-[#b08d57]" />
          </div>
          <h1 className="font-serif-lux text-3xl text-[#16233d] font-semibold" data-testid="auth-title">
            {forgot ? p.forgotTitle : isRegister ? p.registerTitle : p.loginTitle}
          </h1>
          <p className="mt-2 text-[#5c6577] text-sm">{forgot ? p.forgotSubtitle : isRegister ? p.registerSubtitle : p.loginSubtitle}</p>

          {isRegister && !forgot && (
            <Link
              to="/einlagensicherung"
              className="mt-4 flex items-start gap-2 rounded-md bg-[#faf3e8] px-3 py-2.5 text-xs text-[#8f6f3f] hover:text-[#6f5530] transition"
              data-testid="register-deposit-note"
            >
              <ShieldCheck size={15} className="mt-0.5 shrink-0" /> <span>{p.depositNote}</span>
            </Link>
          )}

          {forgot ? (
            <form onSubmit={handleForgot} className="mt-7 space-y-4" data-testid="forgot-form">
              <div className="relative">
                <Mail size={17} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#b08d57]" />
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={p.emailLabel} className={inputCls} data-testid="forgot-email-input" />
              </div>
              <button type="submit" disabled={forgotBusy} className="btn-gold rounded-md w-full py-3.5 font-medium flex items-center justify-center gap-2 disabled:opacity-60" data-testid="forgot-submit-button">
                {p.forgotBtn} <ChevronRight size={17} />
              </button>
              <button type="button" onClick={() => setForgot(false)} className="w-full text-center text-sm font-medium text-[#b08d57] hover:text-[#8f6f3f] transition-colors" data-testid="forgot-back">
                {p.backToLogin}
              </button>
            </form>
          ) : (
          <form onSubmit={submit} className="mt-6 space-y-4" data-testid={isRegister ? "register-form" : "login-form"}>
            {isRegister && (
              <div>
                <label className="block text-xs font-medium text-[#5c6577] mb-1.5">{p.accountTypeLabel}</label>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { key: "private", label: p.accTypePrivate, Icon: User2 },
                    { key: "company", label: p.accTypeCompany, Icon: Building2 },
                  ].map(({ key, label, Icon }) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setAccountType(key)}
                      data-testid={`acctype-${key}`}
                      className={`flex items-center justify-center gap-2 rounded-md border px-3 py-2.5 text-sm font-medium transition ${
                        accountType === key
                          ? "border-[#b08d57] bg-[#faf3e8] text-[#8f6f3f]"
                          : "border-[#e7e2d8] bg-white text-[#5c6577] hover:border-[#d8cdb8]"
                      }`}
                    >
                      <Icon size={16} /> {label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {isRegister && (
              <div className="relative">
                <UserIcon size={17} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#b08d57]" />
                <input value={name} onChange={(e) => setName(e.target.value)} placeholder={p.nameLabel} className={inputCls} data-testid="register-name-input" />
              </div>
            )}
            <div className="relative">
              <Mail size={17} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#b08d57]" />
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={p.emailLabel} className={inputCls} data-testid="auth-email-input" />
            </div>
            <div className="relative">
              <Lock size={17} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#b08d57]" />
              <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder={p.passwordLabel} className={inputCls} data-testid="auth-password-input" />
            </div>

            {isRegister && (
              <>
                <div className="relative">
                  <Globe size={17} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#b08d57]" />
                  <select
                    value={country}
                    onChange={(e) => setCountry(e.target.value)}
                    className={`${inputCls} appearance-none ${country ? "text-[#16233d]" : "text-[#9aa0ad]"}`}
                    data-testid="register-country-select"
                  >
                    <option value="">{p.countryPlaceholder}</option>
                    {COUNTRIES.map((c2) => (
                      <option key={c2} value={c2} className="text-[#16233d]">{c2}</option>
                    ))}
                  </select>
                </div>
                <div className="relative">
                  <Phone size={17} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#b08d57]" />
                  <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder={p.phoneLabel} className={inputCls} data-testid="register-phone-input" />
                </div>

                <label className="flex items-start gap-3 pt-1 cursor-pointer" data-testid="register-consent-label">
                  <input
                    type="checkbox"
                    checked={consent}
                    onChange={(e) => setConsent(e.target.checked)}
                    className="mt-0.5 h-4 w-4 shrink-0 rounded border-[#c9bfa6] text-[#b08d57] accent-[#b08d57] focus:ring-[#b08d57]"
                    data-testid="register-consent-checkbox"
                  />
                  <span className="text-xs leading-relaxed text-[#5c6577]">
                    {p.consentIntro}{" "}
                    <Link to="/agb" className="text-[#b08d57] underline hover:text-[#8f6f3f]">{p.consentAgb}</Link>{" "}
                    {p.consentMid}{" "}
                    <Link to="/datenschutz" className="text-[#b08d57] underline hover:text-[#8f6f3f]">{p.consentPrivacy}</Link>.
                  </span>
                </label>
              </>
            )}

            {!isRegister && (
              <button type="button" onClick={() => setForgot(true)} className="w-full text-right text-sm font-medium text-[#b08d57] hover:text-[#8f6f3f] transition-colors" data-testid="forgot-link">
                {p.forgotPw}
              </button>
            )}
            <button type="submit" disabled={busy} className="btn-gold rounded-md w-full py-3.5 font-medium flex items-center justify-center gap-2 disabled:opacity-60" data-testid="auth-submit-button">
              {isRegister ? p.registerBtn : p.loginBtn} <ChevronRight size={17} />
            </button>
          </form>
          )}

          {!forgot && (
          <div className="mt-5 text-center text-sm text-[#5c6577]">
            {p.noAccount}{" "}
            <Link to="/register" className="font-medium text-[#b08d57] hover:text-[#8f6f3f] transition-colors" data-testid="auth-toggle-mode">
              {p.toRegister}
            </Link>
          </div>
          )}
        </div>

        {!isRegister && !forgot && (
          <div className="mt-5 rounded-xl border border-[#e7e2d8] bg-white p-5">
            <div className="text-xs font-semibold text-[#8f6f3f] uppercase tracking-wide mb-2">{p.demoTitle}</div>
            <p className="text-sm text-[#5c6577]">{p.demoHint}</p>
            <div className="mt-2 text-sm text-[#16233d] font-mono">
              {ACCOUNT.email}<br />{ACCOUNT.password}
            </div>
            <button onClick={fillDemo} className="mt-3 text-sm font-medium text-[#b08d57] hover:text-[#8f6f3f] transition-colors">
              {p.useDemo} →
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function ChangePassword() {
  const c = useContent();
  const p = c.portal;
  const { token } = useAuth();
  const [cur, setCur] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const inputCls =
    "w-full rounded-md border border-[#e7e2d8] bg-white px-4 py-3 text-sm text-[#16233d] placeholder-[#9aa0ad] focus:outline-none focus:border-[#b08d57] focus:ring-2 focus:ring-[#b08d57]/20 transition";

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 6) { toast.error(p.errPassword); return; }
    if (next !== confirm) { toast.error(p.errPwMismatch); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/auth/change-password`, { current_password: cur, new_password: next }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(p.changePwOk);
      setCur(""); setNext(""); setConfirm("");
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(d === "wrong_password" ? p.errWrongPw : d === "same_password" ? p.errSamePw : p.errGeneric);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-[#efe9dd] p-6" data-testid="change-password-card">
      <div className="flex items-center gap-2 mb-1">
        <Lock size={18} className="text-[#b08d57]" />
        <h3 className="font-serif-lux text-xl text-[#16233d]">{p.changePwTitle}</h3>
      </div>
      <p className="text-sm text-[#5c6577] mb-5">{p.changePwSubtitle}</p>
      <form onSubmit={submit} className="space-y-4 max-w-md" data-testid="change-password-form">
        <input type="password" value={cur} onChange={(e) => setCur(e.target.value)} placeholder={p.currentPwLabel} className={inputCls} data-testid="current-password-input" />
        <input type="password" value={next} onChange={(e) => setNext(e.target.value)} placeholder={p.newPwLabel} className={inputCls} data-testid="new-password-input" />
        <input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder={p.confirmPwLabel} className={inputCls} data-testid="confirm-password-input" />
        <button type="submit" disabled={busy} className="btn-gold rounded-md px-6 py-3 text-sm font-medium disabled:opacity-60" data-testid="change-password-submit">
          {p.changePwBtn}
        </button>
      </form>
    </div>
  );
}

function Dashboard() {
  const c = useContent();
  const { lang } = useLang();
  const { user, token, setUser, logout } = useAuth();
  const p = c.portal;

  const [section, setSection] = useState("overview");
  const [modal, setModal] = useState(null); // 'deposit'|'withdraw'|'invest'
  const [amount, setAmount] = useState("");
  const [months, setMonths] = useState(12);
  const [busy, setBusy] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const amt = location.state?.openDeposit;
    if (amt && parseFloat(String(amt).replace(",", ".")) > 0) {
      setAmount(String(amt));
      setModal("deposit");
      window.history.replaceState({}, "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const locale = lang === "de" ? "de-DE" : lang === "fr" ? "fr-FR" : "en-GB";
  const fmt = (n) => new Intl.NumberFormat(locale, { style: "currency", currency: "EUR", maximumFractionDigits: 2 }).format(n || 0);
  const fmtDate = (d) => new Intl.DateTimeFormat(locale, { day: "2-digit", month: "short", year: "numeric" }).format(new Date(d));

  const deposits = useMemo(() => user?.deposits || [], [user]);
  const transactions = useMemo(() => (user?.transactions || []).slice().reverse(), [user]);
  const invested = deposits.reduce((s, d) => s + d.amount, 0);
  const depInterest = (d) => d.amount * (d.rate / 100) * (d.months / 12);

  const H = { Authorization: `Bearer ${token}` };

  const openModal = (type) => { setAmount(""); setMonths(12); setModal(type); };

  const doAction = async () => {
    const amt = parseFloat(String(amount).replace(",", "."));
    if (!amt || amt <= 0) { toast.error(p.invalidAmount); return; }
    setBusy(true);
    try {
      let res;
      if (modal === "deposit") res = await axios.post(`${API}/portal/deposit`, { amount: amt }, { headers: H });
      else if (modal === "withdraw") res = await axios.post(`${API}/portal/withdraw`, { amount: amt }, { headers: H });
      else res = await axios.post(`${API}/portal/invest`, { amount: amt, months }, { headers: H });
      setUser(res.data);
      toast.success(modal === "deposit" ? p.depositOk : modal === "withdraw" ? p.withdrawOk : p.investOk);
      setModal(null);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(d === "insufficient_funds" ? p.insufficient : d === "invalid_amount" ? p.invalidAmount : p.errGeneric);
    } finally {
      setBusy(false);
    }
  };

  const downloadStatement = () => {
    const doc = new jsPDF();
    doc.setFontSize(20); doc.setTextColor(22, 35, 61);
    doc.text("Barlow Consultants SL", 20, 22);
    doc.setFontSize(13); doc.setTextColor(120, 120, 120);
    doc.text(p.statementHeading, 20, 32);
    doc.setFontSize(10); doc.setTextColor(60, 66, 88);
    doc.text(`${user.name}  ·  ${user.email}`, 20, 44);
    doc.text(`${p.accountNumber}: ${user.accountNumber}`, 20, 51);
    doc.text(`${p.generatedOn}: ${fmtDate(new Date())}`, 20, 58);
    doc.setDrawColor(220, 214, 196); doc.line(20, 63, 190, 63);
    doc.setFontSize(11); doc.setTextColor(22, 35, 61);
    doc.text(`${p.availableBalance}: ${fmt(user.balance)}`, 20, 73);
    doc.text(`${p.investedCapital}: ${fmt(invested)}`, 20, 80);
    let y = 94;
    doc.setFontSize(12); doc.text(p.activeDeposits, 20, y); y += 8;
    doc.setFontSize(9); doc.setTextColor(60, 66, 88);
    deposits.forEach((d) => {
      doc.text(`${fmt(d.amount)}  ·  ${d.months} ${p.months}  ·  ${d.rate.toFixed(2)}%  ·  ${p.colMaturity}: ${fmtDate(d.maturity)}`, 20, y);
      y += 6;
    });
    if (!deposits.length) { doc.text("-", 20, y); y += 6; }
    y += 6; doc.setFontSize(12); doc.setTextColor(22, 35, 61); doc.text(p.sbTransactions, 20, y); y += 8;
    doc.setFontSize(9); doc.setTextColor(60, 66, 88);
    transactions.forEach((tx) => {
      const label = tx.type === "deposit" ? p.txDeposit : tx.type === "withdrawal" ? p.txWithdrawal : p.txInvest;
      doc.text(`${fmtDate(tx.date)}  ·  ${label}  ·  ${fmt(tx.amount)}`, 20, y);
      y += 6;
      if (y > 280) { doc.addPage(); y = 20; }
    });
    doc.save(`Barlow_${p.statementHeading}_${user.accountNumber}.pdf`);
  };

  const navItems = [
    { key: "overview", icon: LayoutGrid, label: p.sbOverview },
    { key: "festgeld", icon: Landmark, label: p.sbFestgeld },
    { key: "transaktionen", icon: ArrowLeftRight, label: p.sbTransactions },
    { key: "dokumente", icon: FileText, label: p.sbDocuments },
    { key: "prognose", icon: LineChart, label: p.sbForecast },
    { key: "anlagestrategie", icon: Target, label: p.sbStrategy },
    { key: "sicherheit", icon: ShieldCheck, label: p.sbSecurity },
  ];
  const initials = (user?.name || "").split(" ").map((x) => x[0]).slice(0, 2).join("").toUpperCase();

  return (
    <div className="min-h-screen bg-[#f7f7f9] flex">
      {/* Sidebar (desktop) */}
      <aside className="hidden lg:flex lg:flex-col w-64 shrink-0 bg-white border-r border-[#ececec] sticky top-0 h-screen" data-testid="portal-sidebar">
        <Link to="/" className="flex items-center gap-3 px-6 h-[76px] border-b border-[#f2f0ea] shrink-0">
          <div className="w-10 h-10 rounded-md border border-[#e2d3b3] flex items-center justify-center">
            <span className="font-serif-lux text-xl text-[#b08d57] leading-none">B</span>
          </div>
          <div className="leading-tight">
            <div className="font-serif-lux text-lg text-[#16233d] font-semibold">Barlow</div>
            <div className="text-[10px] tracking-[0.18em] text-[#b08d57] uppercase">Consultants SL</div>
          </div>
        </Link>
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {navItems.map((it) => {
            const Icon = it.icon;
            const active = section === it.key;
            return (
              <button key={it.key} onClick={() => setSection(it.key)} data-testid={`nav-${it.key}`}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-[15px] transition-colors ${active ? "text-[#16233d] font-medium" : "text-[#6b7280] hover:text-[#16233d] hover:bg-[#faf8f4]"}`}>
                <Icon size={18} className={active ? "text-[#16233d]" : "text-[#a0a4ad]"} /> {it.label}
              </button>
            );
          })}
        </nav>
        <div className="p-3 border-t border-[#f2f0ea] space-y-1 shrink-0">
          <div className="px-1 pb-1"><LanguageSwitcher dropUp /></div>
          <Link to="/" className="w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm text-[#5c6577] hover:bg-[#faf8f4] transition-colors">
            <ArrowLeft size={17} className="text-[#9aa0ad]" /> {p.backHome}
          </Link>
          <button onClick={logout} data-testid="portal-logout" className="w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm text-[#5c6577] hover:bg-[#faf8f4] transition-colors">
            <LogOut size={17} className="text-[#9aa0ad]" /> {p.logout}
          </button>
        </div>
      </aside>

      {/* Content */}
      <div className="flex-1 min-w-0 flex flex-col">
        <header className="sticky top-0 z-30 bg-[#f7f7f9]/90 backdrop-blur-sm px-6 lg:px-10 h-[76px] flex items-center justify-between">
          <h1 className="font-serif-lux text-2xl lg:text-3xl text-[#16233d] hidden lg:block">{navItems.find((n) => n.key === section)?.label}</h1>
          <Link to="/" className="flex items-center gap-2 lg:hidden">
            <div className="w-9 h-9 rounded-md border border-[#e2d3b3] flex items-center justify-center"><span className="font-serif-lux text-lg text-[#b08d57]">B</span></div>
            <span className="font-serif-lux text-lg text-[#16233d] font-semibold">Barlow</span>
          </Link>
          <div className="flex items-center gap-3">
            <div className="text-right hidden sm:block">
              <div className="text-sm font-medium text-[#16233d]">{user?.name}</div>
              <div className="text-xs text-[#8a8f9c]">{user?.email}</div>
            </div>
            <div className="w-11 h-11 rounded-full bg-[#16233d] flex items-center justify-center text-white font-serif-lux text-lg" data-testid="user-avatar">{initials}</div>
          </div>
        </header>

        {/* Mobile nav */}
        <nav className="lg:hidden flex gap-1 overflow-x-auto px-4 pb-3">
          {navItems.map((it) => {
            const Icon = it.icon;
            const active = section === it.key;
            return (
              <button key={it.key} onClick={() => setSection(it.key)} data-testid={`mnav-${it.key}`}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm whitespace-nowrap transition-colors ${active ? "bg-[#faf3e8] text-[#8f6f3f] font-medium" : "text-[#5c6577] bg-white border border-[#eee]"}`}>
                <Icon size={16} className={active ? "text-[#b08d57]" : "text-[#9aa0ad]"} /> {it.label}
              </button>
            );
          })}
        </nav>

        <main className="flex-1 px-6 lg:px-10 pb-14 pt-2">

          {section === "overview" && (
            <>
              <div className="mb-6">
                <div className="text-sm text-[#8a8f9c]">{p.welcome}</div>
                <div className="font-serif-lux text-4xl text-[#16233d]">{user?.name}</div>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
                <div className="rounded-xl p-6 bg-[#b08d57] text-white">
                  <div className="text-xs uppercase tracking-wide text-white/70">{p.availableBalance}</div>
                  <div className="font-serif-lux text-3xl mt-3">{fmt(user?.balance)}</div>
                </div>
                <div className="rounded-xl p-6 bg-white border border-[#efe9dd]">
                  <div className="text-xs uppercase tracking-wide text-[#8a8f9c]">{p.investedCapital}</div>
                  <div className="font-serif-lux text-3xl mt-3 text-[#16233d]">{fmt(invested)}</div>
                </div>
                <div className="rounded-xl p-6 bg-white border border-[#efe9dd]">
                  <div className="text-xs uppercase tracking-wide text-[#8a8f9c]">{p.accountNumber}</div>
                  <div className="font-serif-lux text-2xl mt-3 text-[#16233d]">{user?.accountNumber}</div>
                </div>
                <div className="rounded-xl p-6 bg-white border border-[#efe9dd]">
                  <div className="text-xs uppercase tracking-wide text-[#8a8f9c]">{p.statusLabel}</div>
                  <div className="font-serif-lux text-3xl mt-3 text-[#16233d]">{p.statusActive}</div>
                </div>
              </div>

              <div className="mt-6 rounded-xl bg-[#faf9f6] border border-[#ededed] p-5 flex gap-3">
                <ShieldCheck size={22} className="text-[#b08d57] shrink-0 mt-0.5" />
                <p className="text-sm text-[#5c6577] leading-relaxed">
                  <span className="text-[#16233d] font-medium">{p.securityBannerBold}</span> {p.securityBannerText}{" "}
                  <Link to="/einlagensicherung" className="text-[#b08d57] hover:text-[#8f6f3f] transition-colors">{p.securityBannerLink}</Link>. {p.securityBannerExtra}
                </p>
              </div>

              <div className="mt-6 flex flex-wrap gap-3">
                <button onClick={() => openModal("deposit")} data-testid="action-deposit" className="bg-[#16233d] text-white rounded-md px-5 py-3 text-sm font-medium flex items-center gap-2 hover:bg-[#22315a] transition-colors">
                  <ArrowDownToLine size={16} /> {p.deposit}
                </button>
                <button onClick={() => openModal("withdraw")} data-testid="action-withdraw" className="btn-outline-navy rounded-md px-5 py-3 text-sm font-medium flex items-center gap-2">
                  <ArrowUpFromLine size={16} /> {p.requestWithdrawal}
                </button>
                <button onClick={downloadStatement} data-testid="action-statement" className="btn-outline-navy rounded-md px-5 py-3 text-sm font-medium flex items-center gap-2">
                  <Download size={16} /> {p.statementPdf}
                </button>
              </div>
            </>
          )}

          {section === "festgeld" && (
            <div className="bg-white rounded-2xl border border-[#efe9dd] p-6">
              <div className="flex items-center justify-between mb-5">
                <h2 className="font-serif-lux text-2xl text-[#16233d]">{p.activeDeposits}</h2>
                <button onClick={() => openModal("invest")} data-testid="action-invest" className="btn-gold rounded-md px-4 py-2.5 text-sm font-medium flex items-center gap-2">
                  <Plus size={16} /> {p.invest}
                </button>
              </div>
              {deposits.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead><tr className="text-left text-[#8a8f9c] border-b border-[#f0ece3]">
                      <th className="pb-3 font-medium">{p.colAmount}</th><th className="pb-3 font-medium">{p.colTerm}</th>
                      <th className="pb-3 font-medium">{p.colRate}</th><th className="pb-3 font-medium">{p.colMaturity}</th>
                      <th className="pb-3 font-medium text-right">{p.colInterest}</th></tr></thead>
                    <tbody>
                      {deposits.map((d) => (
                        <tr key={d.id} className="border-b border-[#f7f4ee] last:border-0">
                          <td className="py-4 font-medium text-[#16233d]">{fmt(d.amount)}</td>
                          <td className="py-4 text-[#3a4258]">{d.months} {p.months}</td>
                          <td className="py-4 text-[#b08d57] font-medium">{d.rate.toFixed(2)}%</td>
                          <td className="py-4 text-[#3a4258]">{fmtDate(d.maturity)}</td>
                          <td className="py-4 text-right font-medium text-[#16233d]">{fmt(depInterest(d))}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="flex flex-col items-center text-center py-12">
                  <div className="w-14 h-14 rounded-full bg-[#faf3e8] flex items-center justify-center mb-4"><Inbox size={24} className="text-[#b08d57]" /></div>
                  <p className="text-[#5c6577] text-sm max-w-xs">{p.emptyDeposits}</p>
                </div>
              )}
            </div>
          )}

          {section === "transaktionen" && (
            <div className="bg-white rounded-2xl border border-[#efe9dd] p-6">
              {transactions.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead><tr className="text-left text-[#8a8f9c] border-b border-[#f0ece3]">
                      <th className="pb-3 font-medium">{p.colDate}</th><th className="pb-3 font-medium">{p.colType}</th>
                      <th className="pb-3 font-medium">{p.colStatus}</th><th className="pb-3 font-medium text-right">{p.colAmount}</th></tr></thead>
                    <tbody>
                      {transactions.map((tx) => (
                        <tr key={tx.id} className="border-b border-[#f7f4ee] last:border-0">
                          <td className="py-4 text-[#3a4258]">{fmtDate(tx.date)}</td>
                          <td className="py-4 text-[#16233d]">{tx.type === "deposit" ? p.txDeposit : tx.type === "withdrawal" ? p.txWithdrawal : p.txInvest}</td>
                          <td className="py-4"><span className={`text-xs px-2.5 py-1 rounded-full ${tx.status === "completed" ? "bg-[#eaf6ec] text-[#3a9d5d]" : "bg-[#fdf3e3] text-[#b0862f]"}`}>{tx.status === "completed" ? p.statusCompleted : p.statusPending}</span></td>
                          <td className={`py-4 text-right font-medium ${tx.type === "withdrawal" ? "text-[#b45151]" : "text-[#16233d]"}`}>{tx.type === "withdrawal" ? "-" : "+"}{fmt(tx.amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="flex flex-col items-center text-center py-12">
                  <div className="w-14 h-14 rounded-full bg-[#faf3e8] flex items-center justify-center mb-4"><Inbox size={24} className="text-[#b08d57]" /></div>
                  <p className="text-[#5c6577] text-sm">{p.noTransactions}</p>
                </div>
              )}
            </div>
          )}

          {section === "dokumente" && (
            <div className="bg-white rounded-2xl border border-[#efe9dd] p-8 max-w-xl">
              <h2 className="font-serif-lux text-2xl text-[#16233d]">{p.documentsTitle}</h2>
              <p className="text-[#5c6577] text-sm mt-2">{p.documentsDesc}</p>
              <button onClick={downloadStatement} className="btn-gold rounded-md px-5 py-3 text-sm font-medium mt-6 inline-flex items-center gap-2">
                <Download size={16} /> {p.statementPdf}
              </button>
            </div>
          )}

          {section === "prognose" && <Forecast deposits={deposits} p={p} fmt={fmt} />}

          {section === "anlagestrategie" && (
            <div className="grid md:grid-cols-3 gap-5">
              {c.strategies.items.map((s) => (
                <div key={s.name} className="bg-white rounded-2xl border border-[#efe9dd] p-6">
                  <h3 className="font-serif-lux text-2xl text-[#16233d]">{s.name}</h3>
                  <div className="font-serif-lux text-3xl text-[#b08d57] mt-3">{s.ret}</div>
                  <div className="text-xs text-[#8a8f9c] mt-1">{c.strategies.expectedReturn}</div>
                  <p className="text-sm text-[#5c6577] mt-4 leading-relaxed">{s.text}</p>
                </div>
              ))}
            </div>
          )}

          {section === "sicherheit" && (
            <div className="space-y-5 max-w-3xl">
              <ChangePassword />
              {c.security.layers.map((l) => (
                <div key={l.level} className="bg-white rounded-2xl border border-[#efe9dd] p-6">
                  <div className="text-xs font-semibold text-[#8f6f3f] uppercase tracking-wide mb-2">{l.level} · {l.badge}</div>
                  <h3 className="font-serif-lux text-xl text-[#16233d] mb-2">{l.title}</h3>
                  <p className="text-sm text-[#5c6577] leading-relaxed">{l.text}</p>
                </div>
              ))}
            </div>
          )}
        </main>
      </div>

      {/* Action modal */}
      <Dialog open={!!modal} onOpenChange={(v) => !v && setModal(null)}>
        <DialogContent className="sm:max-w-[420px] bg-white">
          <DialogHeader>
            <DialogTitle className="font-serif-lux text-2xl text-[#16233d]">
              {modal === "deposit" ? p.depositTitle : modal === "withdraw" ? p.withdrawTitle : p.investTitle}
            </DialogTitle>
            <DialogDescription className="text-[#5c6577] text-sm">{p.amountLabel}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder={p.amountLabel} data-testid="amount-input"
              className="w-full rounded-md border border-[#e7e2d8] bg-white px-4 py-3 text-sm focus:outline-none focus:border-[#b08d57] focus:ring-2 focus:ring-[#b08d57]/20" />
            {modal === "invest" && (
              <select value={months} onChange={(e) => setMonths(Number(e.target.value))} data-testid="months-select"
                className="w-full rounded-md border border-[#e7e2d8] bg-white px-4 py-3 text-sm focus:outline-none focus:border-[#b08d57]">
                {c.conditions.rows.map((r) => (
                  <option key={r.months} value={r.months}>{r.term} · {r.rate.toFixed(2)}%</option>
                ))}
              </select>
            )}
            <div className="flex gap-3 pt-1">
              <button onClick={() => setModal(null)} data-testid="modal-cancel" className="btn-outline-navy rounded-md px-5 py-3 text-sm font-medium flex-1">{p.cancel}</button>
              <button onClick={doAction} disabled={busy} data-testid="modal-confirm" className="btn-gold rounded-md px-5 py-3 text-sm font-medium flex-1 disabled:opacity-60">{p.confirm}</button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Forecast({ deposits, p, fmt }) {
  const horizon = 10;
  const points = Array.from({ length: horizon + 1 }, (_, y) => ({
    y, value: deposits.reduce((s, d) => s + d.amount * Math.pow(1 + d.rate / 100, y), 0),
  }));
  const maxV = points[points.length - 1].value || 1;
  const w = 640, h = 240, pad = 10;
  const path = points.map((pt, i) => {
    const x = pad + (i / horizon) * (w - pad * 2);
    const yy = h - pad - (pt.value / maxV) * (h - pad * 2);
    return `${i === 0 ? "M" : "L"}${x.toFixed(1)},${yy.toFixed(1)}`;
  }).join(" ");
  const area = `${path} L${w - pad},${h - pad} L${pad},${h - pad} Z`;
  return (
    <div className="bg-[#16233d] rounded-2xl p-8 text-white">
      <h2 className="font-serif-lux text-2xl">{p.forecastTitle}</h2>
      <p className="text-xs text-white/50 mt-1">{p.forecastNote}</p>
      <div className="mt-4"><div className="text-sm text-white/60">{p.afterYears} {horizon} {p.years}</div>
        <div className="font-serif-lux text-4xl text-[#d3b077]">{fmt(maxV)}</div></div>
      <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-auto mt-4">
        <defs><linearGradient id="pgrad2" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#d3b077" stopOpacity="0.35" /><stop offset="100%" stopColor="#d3b077" stopOpacity="0" />
        </linearGradient></defs>
        <path d={area} fill="url(#pgrad2)" /><path d={path} fill="none" stroke="#d3b077" strokeWidth="2.5" />
      </svg>
    </div>
  );
}

function LoadingView() {
  const p = useContent().portal;
  return (
    <div className="min-h-[calc(100vh-5rem)] flex items-center justify-center bg-[#faf8f4] text-[#5c6577]">
      {p.loading}
    </div>
  );
}

export default function Portal({ initialMode = "login" }) {
  const { user, loading } = useAuth();
  if (loading) return (<div className="min-h-screen bg-white"><LoadingView /></div>);
  if (!user) return <Navigate to="/login" replace />;
  return <Dashboard />;
}
